import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function UnderratedVancouverGems() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "underrated-vancouver-gems-downtown";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext();
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "food-finds": "Hidden Food Finds",
    "cultural-gems": "Hidden Cultural Gems",
    "nature-nooks": "Hidden Nature Nooks",
    nightlife: "Hidden Nightlife Spots",
    "local-regulations": "Local Regulations / Best Practices",
    faq: "FAQ",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  const referencesData = [
    {
      url: "https://www.destinationvancouver.com/",
      label: "Destination Vancouver (Official Tourism Site)",
    },
    {
      url: "https://vancouver.ca/",
      label: "City of Vancouver (Official Website)",
    },
    {
      url: "https://www.hellobc.com/",
      label: "Hello BC (Provincial Tourism Board)",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  // Each location id matches a ref in the text sections.
  // lat/lng must be with at least 5 decimals (approx. is fine).
  // popupText is a short label that appears on the map.
  const locations = [
    {
      id: "sashimiya",
      lat: 49.27674,
      lng: -123.13034,
      popupText: "Sashimiya - Hidden Sushi Shop",
    },
    {
      id: "salt-tasting-room",
      lat: 49.283,
      lng: -123.1045,
      popupText: "Salt Tasting Room",
    },
    {
      id: "bill-reid-gallery",
      lat: 49.28476,
      lng: -123.11948,
      popupText: "Bill Reid Gallery",
    },
    {
      id: "macleods-books",
      lat: 49.28333,
      lng: -123.1125,
      popupText: "MacLeod’s Books",
    },
    {
      id: "roedde-house-museum",
      lat: 49.28689,
      lng: -123.13215,
      popupText: "Roedde House Museum",
    },
    {
      id: "dr-sun-yat-sen-park",
      lat: 49.27996,
      lng: -123.1041,
      popupText: "Dr. Sun Yat-Sen Park",
    },
    {
      id: "third-beach",
      lat: 49.3041,
      lng: -123.1567,
      popupText: "Third Beach (Stanley Park)",
    },
    {
      id: "guilt-and-co",
      lat: 49.28366,
      lng: -123.10412,
      popupText: "Guilt & Co (Live Music)",
    },
    {
      id: "the-keefer-bar",
      lat: 49.2798,
      lng: -123.101,
      popupText: "The Keefer Bar",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>Underrated Downtown Vancouver Gems You Must Experience</title>
        <meta
          name="description"
          content="Discover unique hidden spots in Downtown Vancouver. From secret sushi shops to tranquil parks, these underrated gems offer fresh perspectives on Vancouver's urban core."
        />
        <meta
          name="keywords"
          content="Vancouver, hidden gems, downtown, food, culture, nightlife, nature"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/underrated-vancouver-gems-downtown.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Underrated Downtown Vancouver Gems You Must Experience"
        />
        <meta
          property="og:description"
          content="Discover secret sushi shops, tranquil parks, and hidden nightlife spots in Downtown Vancouver."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/underrated-vancouver-gems-downtown.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/underrated-vancouver-gems-downtown.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Underrated Downtown Vancouver Gems You Must Experience"
        />
        <meta
          name="twitter:description"
          content="Explore these hidden gems in Downtown Vancouver for food, nature, culture, and nightlife."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/underrated-vancouver-gems-downtown.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={
                ref_data?.image ||
                "https://bcouver.com/assets/images/article-fallback.jpg"
              }
              alt="Downtown Vancouver Hidden Gems"
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* SECTION: Introduction */}
            <h2 id="intro">Introduction</h2>
            <p>
              Downtown Vancouver is brimming with hidden treasures that even
              many locals haven’t discovered. From secret sushi counters and
              tucked-away bookstores to tranquil garden oases, these underrated
              spots offer an intimate glimpse into Vancouver’s urban heart,
              away from the typical tourist highlights. Below, you’ll find some
              of the most intriguing and less-crowded gems in the downtown area
              that cater to foodies, nature lovers, culture seekers, and
              night-owls alike.
            </p>

            {/* SECTION: Hidden Food Finds */}
            <h2 id="food-finds">Hidden Food Finds</h2>

            <p>
              Vancouver’s known for its diverse culinary scene, but you can
              still find places that feel like your own personal secret. Here
              are two must-try hidden spots if you’re craving something special.
            </p>

            {/* Sashimiya */}
            <p
              ref={(el) => setSpotRef("sashimiya", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Sashimiya</strong>{" "}
              <Marker
                coordY={49.27674}
                coordX={-123.13034}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – 1348 Hornby Street, Vancouver, BC V6Z 1W5.
            </p>
            <p>
              Tucked in a Japanese grocery store’s corner, this spot offers some
              of the freshest, most affordable sushi around. Grab a custom
              platter, or pick from their pre-packed sushi bowls. Tip: Arrive
              around noon for the best selection, and consider taking your meal
              to nearby Sunset Beach for a seaside picnic.
            </p>

            {/* Salt Tasting Room */}
            <p
              ref={(el) => setSpotRef("salt-tasting-room", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Salt Tasting Room</strong>{" "}
              <Marker
                coordY={49.283}
                coordX={-123.1045}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – 45 Blood Alley, Vancouver, BC V6B 1C7.
            </p>
            <p>
              Venture into historic Gastown’s Blood Alley to discover this
              unmarked wine-and-cheese haven. Pair artisan cheeses, charcuterie,
              and condiments with a curated wine list in a rustic-chic
              atmosphere. Staff are happy to help with pairing suggestions if
              you’re not sure which flavors fit best.
            </p>

            {/* SECTION: Hidden Cultural Gems */}
            <h2 id="cultural-gems">Hidden Cultural Gems</h2>
            <p>
              Beyond the popular museums, Downtown Vancouver hosts some quietly
              remarkable cultural spots that speak to the city’s history and
              artistic soul.
            </p>

            {/* Bill Reid Gallery */}
            <p
              ref={(el) => setSpotRef("bill-reid-gallery", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Bill Reid Gallery of Northwest Coast Art</strong>{" "}
              <Marker
                coordY={49.28476}
                coordX={-123.11948}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – 639 Hornby Street, Vancouver, BC V6C 2G3.
            </p>
            <p>
              Canada’s only public gallery dedicated to contemporary Indigenous
              Northwest Coast art. Take a moment to appreciate the intricate
              carvings, jewelry, and sculptures reflecting the local Indigenous
              heritage. Free admission is sometimes offered on the first Friday
              of the month (check schedules).
            </p>

            {/* MacLeod’s Books */}
            <p
              ref={(el) => setSpotRef("macleods-books", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>MacLeod’s Books</strong>{" "}
              <Marker
                coordY={49.28333}
                coordX={-123.1125}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – 455 West Pender Street, Vancouver, BC V6B 1V2.
            </p>
            <p>
              A paradise for bookworms with floor-to-ceiling shelves of new,
              used, and rare titles. The slightly cluttered, old-world vibe
              makes browsing a true adventure. Don’t hesitate to ask the staff
              if you’re looking for something specific—chances are they know
              exactly where to find it!
            </p>

            {/* Roedde House Museum */}
            <p
              ref={(el) => setSpotRef("roedde-house-museum", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Roedde House Museum</strong>{" "}
              <Marker
                coordY={49.28689}
                coordX={-123.13215}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – 1415 Barclay Street, Vancouver, BC V6G 1J6.
            </p>
            <p>
              This Victorian heritage home from 1893, nestled in the West End,
              offers a step back in time. The museum houses thousands of
              historical artifacts and hosts guided tours. On certain Sundays,
              you can enjoy tea on the grounds—a delightful way to combine local
              history with a leisurely afternoon.
            </p>

            {/* SECTION: Hidden Nature Nooks */}
            <h2 id="nature-nooks">Hidden Nature Nooks</h2>
            <p>
              Even in a bustling downtown, there are pockets of tranquility and
              greenery. Check out these lesser-known spots for a refreshing
              break from city life.
            </p>

            {/* Dr. Sun Yat-Sen Park */}
            <p
              ref={(el) => setSpotRef("dr-sun-yat-sen-park", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Dr. Sun Yat-Sen Park</strong>{" "}
              <Marker
                coordY={49.27996}
                coordX={-123.1041}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – 578 Carrall Street, Vancouver, BC V6B 5K2.
            </p>
            <p>
              While the Classical Chinese Garden next door has an entry fee, the
              adjoining park is free and just as serene. Enjoy the koi ponds,
              ornamental plants, and traditional Chinese architecture that offer
              a calm oasis in the heart of Chinatown.
            </p>

            {/* Third Beach */}
            <p
              ref={(el) => setSpotRef("third-beach", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Third Beach</strong>{" "}
              <Marker
                coordY={49.3041}
                coordX={-123.1567}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – Ferguson Point, Stanley Park.
            </p>
            <p>
              Located at Stanley Park’s western edge, Third Beach is a calm,
              sandy hideaway perfect for sunsets. Fewer crowds, drum circles on
              summer Tuesdays, and panoramic ocean views make it a local
              favorite. Pack a picnic and stay for star-gazing if the sky is
              clear—just remember to mind park hours and travel in a group or
              well-lit path after dark.
            </p>

            {/* SECTION: Hidden Nightlife Spots */}
            <h2 id="nightlife">Hidden Nightlife Spots</h2>
            <p>
              Vancouver’s nightlife may be overshadowed by its daytime scenery,
              but the city does offer some unique after-dark experiences.
            </p>

            {/* Guilt & Co */}
            <p
              ref={(el) => setSpotRef("guilt-and-co", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Guilt & Co</strong>{" "}
              <Marker
                coordY={49.28366}
                coordX={-123.10412}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – 1 Alexander Street, Vancouver, BC V6A 1B2.
            </p>
            <p>
              A cozy basement speakeasy in Gastown known for its nightly live
              music—jazz, funk, and everything in between. Arrive early to get a
              seat; the place fills up quickly once bands start. Pro tip: Keep
              cash handy to tip the musicians.
            </p>

            {/* The Keefer Bar */}
            <p
              ref={(el) => setSpotRef("the-keefer-bar", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>The Keefer Bar</strong>{" "}
              <Marker
                coordY={49.2798}
                coordX={-123.101}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – 135 Keefer Street, Vancouver, BC V6A 1X3.
            </p>
            <p>
              On the edge of Chinatown, The Keefer Bar channels an apothecary
              vibe with herbal tinctures in its inventive cocktails. The dimly
              lit interior and vintage medicine-bottle décor give it an intimate
              feel. Be prepared for a line on weekends, but the experience is
              worth the wait.
            </p>

            {/* SECTION: Local Regulations / Best Practices */}
            <h2 id="local-regulations">Local Regulations / Best Practices</h2>
            <p>
              <strong>Park Etiquette:</strong> Vancouver bans smoking in all
              city parks and beaches. Avoid feeding wildlife; it’s illegal and
              can lead to fines. Stick to designated paths and place trash in
              the proper bins.
            </p>
            <p>
              <strong>Hours & Safety:</strong> Many parks are open from dawn to
              dusk. Stanley Park can be accessed 24/7, but be mindful of the
              darkness and safety after sunset. Downtown is generally safe,
              though it’s wise to walk in groups or along well-lit streets at
              night.
            </p>
            <p>
              <strong>Transit & Identification:</strong> Downtown is very
              walkable, and public transit is widely available. If visiting bars
              like Guilt & Co or The Keefer Bar, you must be 19+ and carry
              official ID.
            </p>
            <p>
              <strong>Leave No Trace:</strong> Whether you’re on the beach or in
              a museum, help keep these hidden gems pristine by disposing of
              waste responsibly and respecting the space.
            </p>

            {/* SECTION: FAQ */}
            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>1. Do I need a car to explore these spots?</h3>
              <p>
                No. Downtown Vancouver is walkable, and transit (SkyTrain,
                buses) plus ride-shares make it easy to get around. Many of
                these locations are within a short distance of each other.
              </p>

              <h3>2. Are these places free?</h3>
              <p>
                It varies. Nature spots like Third Beach or Dr. Sun Yat-Sen Park
                are free; museums and galleries may have small admission fees.
                Bars and restaurants have no cover most nights, but you’ll pay
                for food or drinks.
              </p>

              <h3>3. Is Downtown Vancouver safe at night?</h3>
              <p>
                Generally, yes. Standard city precautions apply: stick to
                well-lit areas, walk with a friend, and keep your belongings
                secure. Gastown and Chinatown may have some rough edges, but
                they’re still popular nightlife hubs.
              </p>
            </div>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              Downtown Vancouver is far more than its famous waterfront and
              bustling Robson Street. By venturing off the typical paths, you’ll
              discover a world of surprises—from hidden sushi shops and
              centuries-old heritage homes to underground music clubs and
              tranquil gardens. Whether you’re visiting for the first time or
              looking to rediscover your hometown, these underrated gems will
              give you a fresh perspective on Vancouver’s vibrant downtown. Now,
              it’s time to explore!
            </p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay
        ref={mobileMapRef}
        markers={locations}
        spotRefs={spotRefs}
      />

      <Footer />
    </>
  );
}